import {UIAjaxWrapper} from '../../../shared/scripts/network/UIAjaxWrapper.ts'
import {UIPopupManager} from '../../../shared/scripts/ui_popup/UIPopupManager.ts'
import {UIMSceneEnum} from '../../../../shared/enums/UIMSceneEnums.ts'
import {type UIMMainPageController} from '../ui_html/main_page/UIMMainPageController.ts'
import {UIMFullscreenPageController} from './UIMFullscreenPageController.ts'
import {type MatchType, type ReplayInitiationData} from '@colonist/game-base-types'
import {UIReplayController} from '../../../shared/scripts/ui_replay/UIReplayController.ts'
import {UIGameSpriteHandler} from '../../../shared/scripts/UIGameSpriteHandler.ts'
import {socket} from '../../../shared/scripts/EntryShared.ts'
import {getReplayDataUrl} from '../../../shared/scripts/ui_replay/ReplayUrlUtilities.ts'
import {ClientUserState} from '../../../shared/scripts/ClientUserState.ts'
import {UIMViewPageNavigationWarningPopup} from '../ui_popup/UIMViewPageNavigationWarningPopup.ts'
import {uiManager} from '../EntryMobile.ts'
import {navigateToPage} from '../../../shared/scripts/ui_utils/ClientUtils.ts'
import {UIUrlParameterNames} from '../../../shared/scripts/ui_html/UIUrlController.ts'
import {UIEntryUrlParameterController} from '../../../shared/scripts/UIEntryUrlParameterController.ts'
import {UIViewProfilePopup} from '../../../shared/scripts/profile/UIViewProfilePopup.ts'
import {matchMobileRoute} from './MobileRouteResolver.ts'

export class UIMSingletonPageController extends UIMFullscreenPageController {
    static routesListener: (event: MouseEvent) => void
    private readonly mainpageController: UIMMainPageController

    constructor(mainpageController: UIMMainPageController) {
        super()
        this.mainpageController = mainpageController
        this.setLinkListeners()
        this.updatePageOnHashChange()
    }

    handleUrlRedirect(href: string): boolean {
        const pathname = UIMSingletonPageController.getSingletonPageUrl(href)
        const hash = href.split('#')[1] ?? ''
        if(!pathname) return false

        if(hash == `${UIUrlParameterNames.MainPage}=${UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE}`) {
            this.redirectToScene(UIMSceneEnum.Homepage)
            return true
        }

        if(hash.startsWith(`${UIUrlParameterNames.SelectMode}=`)) {
            const requestedMatchType = hash.split('=')[1] as MatchType

            this.redirectToScene(UIMSceneEnum.Homepage)
            void uiManager.goToMainPageAndSelectMatchType(requestedMatchType)
            return true
        }

        if(hash == `${UIUrlParameterNames.Spectate}=${UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE}`) {
            this.redirectToScene(UIMSceneEnum.Lobby)
            this.mainpageController.bodyController.lobbyController.tableController.headerController.openSpectateTab()
            return true
        }

        const matchedRoute = matchMobileRoute(pathname)
        if(matchedRoute == undefined) return false

        switch(matchedRoute) {
            case '/store':
            case '/checkout':
                this.redirectToScene(UIMSceneEnum.Store)
                if(hash) this.mainpageController?.bodyController?.storeController?.sidebarController?.openDefaultStorePage(`#${hash}`)
                return true
            case '/leaderboards':
                this.redirectToScene(UIMSceneEnum.Leaderboard)
                return true
            case '/profile': {
                const showedProfilePopup = this.shouldOpenProfilePopup(href)
                if(!showedProfilePopup) {
                    new UIMViewPageNavigationWarningPopup(() => {
                        if(uiManager?.mainpageController?.sceneController?.scene == UIMSceneEnum.Game) {
                            return navigateToPage(`/profile/${ClientUserState.userState?.username?.toLowerCase()}`)
                        } else {
                            this.closePage()
                            uiManager.mainpageController.profileOverlayController.show()
                        }
                    })
                }
                return true
            }
            case '/replay':
                new UIMViewPageNavigationWarningPopup(() => {
                    if(uiManager?.mainpageController?.sceneController?.scene == UIMSceneEnum.Game) {
                        return navigateToPage(href)
                    }

                    const replayDataUrl = getReplayDataUrl(href)
                    this.loadReplay(replayDataUrl)
                })
                return true
            case '/catan-rules':
                // eslint-disable-next-line no-restricted-syntax
                window.location.href = '/catan-rules'
                return true
            default:
                this.openUrl(matchedRoute)
                return true
        }
    }

    // eslint-disable-next-line class-methods-use-this
    private shouldOpenProfilePopup(href: string): boolean {
        const profileUsername = UIMSingletonPageController.getProfileUsername(href)
        if(!profileUsername) return false
        if(profileUsername.toLowerCase() == ClientUserState.userState?.username?.toLowerCase()) return false

        UIViewProfilePopup.init(profileUsername)
        return true
    }

    static getProfileUsername(href: string): string | undefined {
        if(!href.includes('/profile')) return
        const fullUrl = href.startsWith('http') ? href : window.location.origin + href
        const url = new URL(fullUrl)
        const pathnameParts = url.pathname.split('/')
        const username = pathnameParts[pathnameParts.length - 1]
        if(!username) return
        return username
    }

    private setLinkListeners() {
        UIMSingletonPageController.routesListener = (event: MouseEvent) => {
            const clickedElement = event.target as HTMLElement
            if(clickedElement == undefined) return
            const href = UIMSingletonPageController.getElementHref(clickedElement)
            if(!href) return

            const pageUrl = UIMSingletonPageController.getSingletonPageUrl(href)
            if(!pageUrl) return

            event.preventDefault()

            if(pageUrl.startsWith('#')) {
                const hash = href.split('#')[1]
                if(hash) {
                    const elementToScrollTo = document.getElementById(hash)
                    elementToScrollTo?.scrollIntoView({behavior: 'smooth'})
                }
                return
            }

            if(pageUrl.includes('/static')) return
            if(pageUrl.includes('/dist')) return

            UIPopupManager.closeAllPopups()
            this.mainpageController?.headerController.notificationPanelController?.hideNotificationPanel()
            this.mainpageController?.sceneController.closeOverlays()

            const isRedirected = this.handleUrlRedirect(href)
            if(isRedirected) return

            this.openUrl(pageUrl)
        }

        document.addEventListener('click', UIMSingletonPageController.routesListener)
    }

    // eslint-disable-next-line class-methods-use-this
    removeLinkListener() {
        document.removeEventListener('click', UIMSingletonPageController.routesListener)
    }

    private loadReplay(replayUrl: string) {
        void UIGameSpriteHandler.continueAfterAssetsLoad(async() => {
            const replayDataUrl = replayUrl
            const replayDataResponse = await UIAjaxWrapper.getRequestHandler(replayDataUrl, undefined)

            const replayInitiationData = replayDataResponse?.json.data as ReplayInitiationData | undefined
            if(replayInitiationData === undefined) throw new Error('Missing replayData')

            this.redirectToScene(UIMSceneEnum.Game)
            const isLiveGame = false
            new UIReplayController(replayInitiationData, socket.actionController, isLiveGame)
        })
    }

    static getElementHref(clickedElement: HTMLElement) {
        const element = clickedElement.tagName
        const isATag = element == 'A'
        if(isATag) return clickedElement.getAttribute('href')

        return clickedElement.parentElement?.getAttribute('href')
    }

    static getSingletonPageUrl(href: string): string | undefined {
        const urlContainsSiteDomain = href.startsWith(window.location.origin)
        if(urlContainsSiteDomain) return new URL(href).pathname

        const isHash = href.startsWith('#')
        if(isHash) return href

        const urlIsRelative = href.startsWith('/')
        if(!urlIsRelative) return

        if(href.includes('catan-rules')) return
        if(href.includes('/auth/')) return
        return href
    }

    private redirectToScene(scene: UIMSceneEnum) {
        this.mainpageController.sceneController.loadScene(scene)
        this.closePage()
        return
    }

    // eslint-disable-next-line class-methods-use-this
    private updatePageOnHashChange() {
        window.onhashchange = (hashChangeEvent: HashChangeEvent) => {
            const oldUrl = new URL(hashChangeEvent.oldURL)
            const newUrl = new URL(hashChangeEvent.newURL)

            const isTheSameHash = oldUrl.hash.toLocaleLowerCase() == newUrl.hash.toLocaleLowerCase()
            if(isTheSameHash) return

            const hashesNotEmpty = oldUrl.hash != '' && newUrl.hash != ''

            if(!hashesNotEmpty) return
            location.reload()
        }
    }
}
